/**
 * Debt Instrument Popup Handlers
 *
 * This module contains all the popup/modal logic for debt instruments:
 * - Altrau Z Bills
 * - M Notes
 * - Z-Payday CDOs
 * - V Notes
 * - Generic debt notifications
 */

import { showHoloModal, holoNotify } from './holoModal.js';
import { qualitativeFromFraction_LOCAL } from './socialEconomyHandlers.js';
import { t } from './translationHelper.js';

/**
 * Helper function to get qualitative label from fraction
 */
const qLabel = (num, denom = 100) => {
  const n = Number(num) || 0;
  const d = Math.max(1, Number(denom) || 100);
  return qualitativeFromFraction_LOCAL(Math.max(0, Math.min(1, n / d)));
};

/**
 * Helper to get descriptive amount for M Notes
 */
const getDescriptiveAmount = (amt) => {
  if (!amt || amt <= 0) return t('debt.amounts.none', 'None');
  if (amt <= 1)   return t('debt.amounts.barelyAny', 'Barely Any');
  if (amt <= 2)   return t('debt.amounts.aTrace', 'A Trace');
  if (amt <= 3)   return t('debt.amounts.aHint', 'A Hint');
  if (amt <= 5)   return t('debt.amounts.aLittle', 'A Little');
  if (amt <= 8)   return t('debt.amounts.aFew', 'A Few');
  if (amt <= 12)  return t('debt.amounts.some', 'Some');
  if (amt <= 18)  return t('debt.amounts.several', 'Several');
  if (amt <= 25)  return t('debt.amounts.moderate', 'Moderate');
  if (amt <= 35)  return t('debt.amounts.noticeable', 'Noticeable');
  if (amt <= 45)  return t('debt.amounts.considerable', 'Considerable');
  if (amt <= 60)  return t('debt.amounts.many', 'Many');
  if (amt <= 80)  return t('debt.amounts.aLot', 'A Lot');
  if (amt <= 100) return t('debt.amounts.abundant', 'Abundant');
  if (amt <= 150) return t('debt.amounts.heaps', 'Heaps');
  if (amt <= 200) return t('debt.amounts.aGreatDeal', 'A Great Deal');
  if (amt <= 300) return t('debt.amounts.substantial', 'Substantial');
  return t('debt.amounts.immense', 'Immense');
};

/**
 * Helper to get descriptive interest rate for M Notes
 */
const getDescriptiveInterest = (rate) => {
  const pct = rate * 100;
  if (pct <= 5)  return t('debt.interest.minimal', 'Minimal Emotional Interest');
  if (pct <= 10) return t('debt.interest.light', 'Light Emotional Interest');
  if (pct <= 15) return t('debt.interest.modest', 'Modest Emotional Interest');
  if (pct <= 20) return t('debt.interest.fair', 'Fair Emotional Interest');
  if (pct <= 25) return t('debt.interest.moderate', 'Moderate Emotional Interest');
  if (pct <= 30) return t('debt.interest.notable', 'Notable Emotional Interest');
  if (pct <= 35) return t('debt.interest.considerable', 'Considerable Emotional Interest');
  if (pct <= 40) return t('debt.interest.heavy', 'Heavy Emotional Interest');
  if (pct <= 50) return t('debt.interest.intense', 'Intense Emotional Interest');
  return t('debt.interest.crushing', 'Crushing Emotional Interest');
};

/**
 * Helper to get descriptive amount for V Notes
 */
const getDescriptiveAmountVNote = (amt) => {
  if (!amt || amt <= 0) return t('debt.amounts.none', 'None');
  if (amt <= 10)  return t('debt.amounts.aHint', 'A Hint');
  if (amt <= 30)  return t('debt.amounts.aFew', 'A Few');
  if (amt <= 60)  return t('debt.amounts.some', 'Some');
  if (amt <= 100) return t('debt.amounts.several', 'Several');
  if (amt <= 150) return t('debt.amounts.noticeable', 'Noticeable');
  if (amt <= 200) return t('debt.amounts.considerable', 'Considerable');
  return t('debt.amounts.many', 'Many');
};

/**
 * Helper to format numbers for CDO display
 */
const fmtNum = (n) => {
  const v = Number(n);
  return Number.isFinite(v) ? v.toLocaleString() : String(n ?? '');
};

/**
 * Helper to resolve zollar count for CDO
 */
const resolveZCount = (raw) => {
  if (Array.isArray(raw)) return raw.length;
  if (typeof raw === 'object' && raw !== null) {
    return Object.values(raw).reduce((a, b) => a + (Number(b) || 0), 0);
  }
  return Number(raw);
};

/**
 * Refreshes the emotional debt meter if available
 */
const refreshEmotionalMeter = () => {
  if (window.emotionalMeter) {
    window.emotionalMeter.fetchData();
  }
};

/**
 * Refreshes portfolio data if the function is available
 */
const refreshPortfolioData = () => {
  if (typeof loadPortfolioData === 'function') {
    loadPortfolioData();
  }
};

// ============================================================================
// ALTRAU Z BILL HANDLERS
// ============================================================================

/**
 * Handler for when user receives an Altrau Z Bill
 */
export function handleAltrauZBillReceived(data) {
  console.log('Z Bill Altrau Received:', data);
  const { from, amount, type, instrumentId, perceivedLabel, zollarBreakdown } = data;

  // Prefer the backend's qualitative label, otherwise generate one
  let label = perceivedLabel;
  if (!label) {
    if (type === 'zollars') label = qLabel(amount, 100);
    else if (type === 'capital' || type === 'points') label = qLabel(amount, 1000);
  }

  const unitText = type === 'zollars' ? 'Ƶ' : t('debt.capital', 'capital');
  const giftText = label ? `${label} ${unitText}` : `${amount} ${type}`;

  showHoloModal({
    title: t('debt.altrau.receivedTitle', 'Z Bill Altrau Received'),
    message: `<p><strong>${from}</strong> ${t('debt.altrau.hasGivenYou', 'has given you')} <strong>${giftText}</strong>.</p>
              <p>${t('debt.altrau.ledgersBound', 'Your ledgers are now bound. Their Life Profit affects yours.')}</p>`,
    type: 'success'
  });

  refreshEmotionalMeter();
}

/**
 * Handler for when user gives an Altrau Z Bill
 */
export function handleAltrauZBillGiven(data) {
  console.log('Z Bill Altrau issued:', data);
  const { to, amount, type, perceivedLabel } = data;

  const normType = (type || '').toLowerCase();
  let label = perceivedLabel;

  if (!label) {
    if (normType === 'zollars') {
      label = qLabel(amount, 100);
    } else if (normType === 'capital' || normType === 'points') {
      label = qLabel(amount, 1000);
    } else {
      // fallback: treat unknown as capital scale
      label = qLabel(amount, 1000);
    }
  }

  const unitText = normType === 'zollars' ? 'Ƶ' : t('debt.capital', 'capital');
  const giftText = `${label} ${unitText}`;

  showHoloModal({
    title: t('debt.altrau.issuedTitle', 'Z Bill Altrau Issued'),
    message: `<p>${t('debt.altrau.youHaveSent', 'You have sent')} <strong>${giftText}</strong> ${t('debt.altrau.toRecipient', 'to')} <strong>${to}</strong>.</p>
              <p>${t('debt.altrau.fatesBound', 'Your fates are now bound. Your Life Profit affects theirs.')}</p>`,
    type: 'success'
  });

  refreshEmotionalMeter();
}

// ============================================================================
// M NOTE HANDLERS
// ============================================================================

/**
 * Handler for M Note creation
 */
export function handleMNoteCreated(data) {
  console.log('💭 [Frontend] ========================================');
  console.log('💭 [Frontend] M Note created event received!');
  console.log('💭 [Frontend] Data:', JSON.stringify(data, null, 2));
  const { role, from, to, amount, interestRate, instrumentId, message } = data;
  const title = role === 'borrower'
    ? t('debt.mnote.acceptedTitle', 'M Note Accepted')
    : t('debt.mnote.createdTitle', 'M Note Created');
  console.log('💭 [Frontend] Title:', title);
  console.log('💭 [Frontend] Role:', role);

  const msg = role === 'borrower'
    ? `<p><strong>${from}</strong> ${t('debt.mnote.witnessedVulnerability', 'has witnessed your vulnerability')}.</p><p>${t('debt.mnote.loanLabel', 'Loan')}: <strong>${getDescriptiveAmount(amount)}</strong> ${t('debt.mnote.atRate', 'at')} <strong>${getDescriptiveInterest(interestRate || 0.26)}</strong>.</p><p style="font-size:0.9em;color:#3dd202;margin-top:8px;">💡 ${t('debt.mnote.positiveTradeTip', 'Make positive trades to automatically reduce this debt!')}</p>`
    : `<p>${t('debt.mnote.createdWith', 'M Note created with')} <strong>${to}</strong>.</p><p>${t('debt.mnote.tradeableAfter', 'Tradeable after 5 exchanges. Convertible after max trades.')}</p>`;

  console.log('💭 [Frontend] Message HTML:', msg);
  console.log('💭 [Frontend] showHoloModal defined?', typeof showHoloModal === 'function');
  console.log('💭 [Frontend] Calling showHoloModal...');
  showHoloModal({ title, message: msg, type: 'success' });
  console.log('💭 [Frontend] showHoloModal called successfully');

  refreshEmotionalMeter();
  console.log('💭 [Frontend] ========================================');
}

/**
 * Handler for M Note repayment progress
 */
export function handleMNoteProgress(data) {
  console.log('📊 M Note progress:', data);
  const { milestone, percentRepaid, remainingAmount, message } = data;
  holoNotify(
    `<p><strong>${t('debt.mnote.progressTitle', 'M Note Repayment Progress')}</strong></p>
     <p>${t('debt.mnote.repaidPercent', "You've repaid")} <strong>${milestone}%</strong> ${t('debt.mnote.throughTrading', 'through positive trading')}!</p>
     <p style="font-size:0.9em;color:#999;">${t('debt.mnote.keepTrading', 'Keep trading well to continue reducing your debt.')}</p>`,
    'success'
  );
  refreshPortfolioData();
}

/**
 * Handler for M Note fully repaid
 */
export function handleMNoteRepaid(data) {
  console.log('✅ M Note repaid:', data);
  const { role, instrumentId, borrowerName, lenderName, message, repaymentMethod } = data;
  const title = role === 'borrower'
    ? t('debt.mnote.debtClearedTitle', 'Debt Cleared!')
    : t('debt.mnote.repaidTitle', 'M Note Repaid');

  const repaymentMethodText = repaymentMethod === 'trading'
    ? t('debt.mnote.positiveTrading', 'positive trading')
    : t('debt.mnote.directRepayment', 'direct repayment');

  const msg = role === 'borrower'
    ? `<p>🎉 ${t('debt.mnote.fullyRepaidTo', "You've fully repaid your M Note to")} <strong>${lenderName}</strong> ${t('debt.mnote.throughMethod', 'through')} ${repaymentMethodText}!</p><p>${t('debt.mnote.emotionalDebtCleared', 'Your emotional debt is cleared. Well done!')}</p>`
    : `<p><strong>${borrowerName}</strong> ${t('debt.mnote.hasFullyRepaid', 'has fully repaid their M Note')} ${t('debt.mnote.throughMethod', 'through')} ${repaymentMethodText}.</p><p>${t('debt.mnote.instrumentClosed', 'The debt instrument has been closed.')}</p>`;

  showHoloModal({ title, message: msg, type: 'success' });
  refreshPortfolioData();
}

/**
 * Handler for M Note trade alert
 */
export function handleMNoteTradeAlert(data) {
  console.log('🔔 M Note trade alert:', data);
  const { borrowerName, lenderName, tradesRemaining, message } = data;
  holoNotify(
    `<p><strong>${t('debt.mnote.alertTitle', 'M Note Alert')}</strong></p><p>${message}</p>`,
    'warning'
  );
}

/**
 * Handler for M Note becoming convertible
 */
export function handleMNoteConvertible(data) {
  console.log('✨ M Note convertible:', data);
  const { borrowerName, amount, message } = data;
  showHoloModal({
    title: t('debt.mnote.convertibleTitle', 'M Note Convertible'),
    message: `<p>${t('debt.mnote.nowConvertible', 'Your M Note is now')} <strong>${t('debt.mnote.convertibleCaps', 'CONVERTIBLE')}</strong>!</p><p>${t('debt.mnote.canConvertTo', 'You can convert it to')} <strong>${amount}</strong> ${t('debt.mnote.inLiquidAssets', 'in liquid assets')}.</p><p style="font-size:0.9em;color:#fcba03;margin-top:8px;">${t('socketFront.checkDebtHeldSection', 'Check your Debt Held section to convert.')}</p>`,
    type: 'success'
  });
}

// ============================================================================
// Z-PAYDAY CDO HANDLERS
// ============================================================================

/**
 * Handler for Z-Payday CDO creation
 */
export function handleZPaydayCDOCreated(data) {
  console.log('☠️ Z-Payday CDO created:', data);
  const {
    role,
    from,
    to,
    amount,
    instrumentId,
    message,
    principalType,
    zollarCount,
    pkg
  } = data;

  // Try to show zollars if provided; otherwise fall back to capital
  let headline;
  if (String(principalType).toLowerCase() === 'zollars') {
    let count = resolveZCount(zollarCount);
    if (!Number.isFinite(count) || count <= 0) {
      const raw = pkg?.zollars;
      const derived = resolveZCount(raw);
      count = Number.isFinite(derived) && derived > 0 ? derived : Number(amount) || 0;
    }
    // Use qualitative descriptor for zollars (assume max ~100 for context)
    const qualitativeLabel = qLabel(count, 100);
    headline = `<strong>${qualitativeLabel} Ƶ</strong> ${t('debt.cdo.availableImmediately', 'available immediately')}!`;
  } else {
    headline = `<strong>${fmtNum(amount)} ${t('debt.capital', 'capital')}</strong> ${t('debt.cdo.availableImmediately', 'available immediately')}!`;
  }

  if (role === 'victim') {
    showHoloModal({
      title: t('debt.cdo.acceptedTitle', 'Z-Payday CDO Accepted'),
      message: `<p><strong>${from}</strong> ${t('debt.cdo.offeredLoan', 'has offered you a loan')}.</p>
                <p style="font-size:1.1em;color:#3dd202;margin:10px 0;">${headline}</p>
                <p>${t('socketFront.debtWarningPrefix', 'WARNING: This debt reloads every 60 seconds at 15% compound until repayment.')}</p>`,
      type: 'danger',
      autoClose: false
    });
  } else {
    showHoloModal({
      title: t('debt.cdo.createdTitle', 'Z-Payday CDO Created'),
      message: `<p>${t('debt.cdo.createdWith', 'Z-Payday CDO created with')} <strong>${to}</strong>.</p>
                <p>${t('debt.cdo.tradeableCompounds', 'Tradeable. Compounds every 60s at 15%.')}</p>`,
      type: 'warning'
    });
  }
  refreshEmotionalMeter();
}

/**
 * Handler for Z-Payday CDO reload (compound interest cycle)
 */
export function handleZPaydayCDOReloaded(data) {
  console.log('💸 Z-Payday CDO reloaded:', data);
  const { role, cycle, newDebt, totalDebt, isBankrupt, message } = data;
  const modalType = isBankrupt ? 'danger' : (role === 'victim' ? 'warning' : 'success');
  const title = isBankrupt
    ? t('debt.cdo.bankruptcyWarningTitle', 'BANKRUPTCY WARNING')
    : t('debt.cdo.reloadCycle', 'CDO Reload: Cycle {cycle}').replace('{cycle}', cycle);

  const msg = role === 'victim'
    ? `<p>${t('socketFront.debtCompounded', 'Debt compounded:')} <strong>+${newDebt}</strong></p><p>${t('socketFront.totalDebt', 'Total debt:')} <strong>${totalDebt}</strong></p>${isBankrupt ? `<p style="color:#ff4d4d;">${t('socketFront.bankruptcyWarning', 'WARNING: You are approaching bankruptcy!')}</p>` : ''}`
    : `<p>${t('debt.cdo.cdoCompounded', 'Your CDO compounded')}.</p><p>${t('debt.cdo.victimOwes', 'Victim owes')}: <strong>${totalDebt}</strong></p>`;

  holoNotify(`${title}: ${message}`, modalType);
  refreshEmotionalMeter();
}

/**
 * Handler for Z-Payday CDO bankruptcy
 */
export function handleZPaydayCDOBankruptcy(data) {
  console.log('💥 Z-Payday CDO bankruptcy:', data);
  const { role, totalDebt, seized, message } = data;
  if (role === 'victim') {
    showHoloModal({
      title: t('debt.cdo.bankruptcyTitle', 'BANKRUPTCY'),
      message: `<p><strong>${t('debt.cdo.cdoCollapsed', 'Your Z-Payday CDO collapsed')}.</strong></p><p>${t('debt.cdo.totalDebtLabel', 'Total debt')}: <strong>${totalDebt}</strong></p><p>${t('debt.cdo.assetsSeized', 'Assets seized')}: <strong>${seized}</strong> ${t('debt.capital', 'capital')}</p><p style="color:#ff4d4d;">${t('debt.cdo.standingDamaged', 'Your financial standing has been severely damaged.')}</p>`,
      type: 'danger',
      autoClose: false
    });
  } else {
    showHoloModal({
      title: t('debt.cdo.maturedTitle', 'CDO Matured'),
      message: `<p>${t('debt.cdo.victimBankrupt', 'Your victim declared bankruptcy')}.</p><p>${t('debt.cdo.youSeized', 'You seized')}: <strong>${seized}</strong> ${t('debt.capital', 'capital')}</p><p style="color:#fcba03;">${t('debt.cdo.toxicGainRecorded', 'Toxic relationship gain recorded.')}</p>`,
      type: 'success'
    });
  }
  refreshEmotionalMeter();
}

// ============================================================================
// V NOTE HANDLERS
// ============================================================================

/**
 * Handler for V Note granted (requester receives aid)
 */
export function handleVNoteGranted(data) {
  console.log('🙏 V Note granted:', data);
  const { from, amount, message } = data;

  const descriptiveAmount = getDescriptiveAmountVNote(amount);

  showHoloModal({
    title: t('debt.vnote.aidGrantedTitle', 'V Note Aid Granted'),
    message: `<p><strong>${from}</strong> ${t('debt.vnote.hasGrantedAid', 'has granted you aid')}!</p>
      <p style="font-size:1.1em;color:#3dd202;margin:10px 0;">${t('debt.vnote.received', 'Received')}: <strong>${descriptiveAmount} ${t('debt.capital', 'capital')}</strong> (${amount})</p>
      <p style="font-size:0.9em;color:#fcba03;">${t('debt.vnote.vulnerabilityWitnessed', 'Your vulnerability was witnessed and met with compassion.')}</p>`,
    type: 'success'
  });
  refreshEmotionalMeter();
  refreshPortfolioData();
}

/**
 * Handler for V Note grant confirmation (granter confirms giving)
 */
export function handleVNoteGrantConfirm(data) {
  console.log('✅ V Note grant confirmed:', data);
  const { to, amount, message } = data;

  const descriptiveAmount = getDescriptiveAmountVNote(amount);

  showHoloModal({
    title: t('debt.vnote.aidGrantedTitle', 'Aid Granted'),
    message: `<p>${t('debt.vnote.youGranted', 'You granted')} <strong>${descriptiveAmount} ${t('debt.capital', 'capital')}</strong> (${amount}) ${t('debt.vnote.toRecipient', 'to')} <strong>${to}</strong>.</p>
      <p style="font-size:0.9em;color:#fcba03;">${t('debt.vnote.compassionRecorded', 'Your compassion has been recorded in the social ledger.')}</p>`,
    type: 'success'
  });
  refreshEmotionalMeter();
  refreshPortfolioData();
}

// ============================================================================
// GENERIC DEBT NOTIFICATIONS
// ============================================================================

/**
 * Handler for generic debt notifications
 */
export function handleDebtNotification(data) {
  console.log('💸 Debt notification received:', data);
  const { showNotification } = require('./notif.js');

  if (data.type === 'debt_warning') {
    showNotification(`${t('socketFront.warningPrefix', 'WARNING:')} ${data.message}`, false);
  } else if (data.type === 'default_warning') {
    showNotification(`${t('socketFront.criticalPrefix', 'CRITICAL:')} ${data.message}`, false);
  } else if (data.type === 'margin_notice') {
    showNotification(`${data.message}`, true);
  } else if (data.type === 'payment_received') {
    showNotification(`${data.message}`, true);
  } else if (data.type === 'recovery_offered') {
    showNotification(`${data.message} ${t('socketFront.debtPageVisit', 'Visit the Debt page for details.')}`, true);
  }
}

/**
 * Handler for debt wipeout event
 */
export function handleDebtWipeout(data) {
  console.log('💥 Debt wipeout event received:', data);
  const { showNotification } = require('./notif.js');

  showNotification(data.message || t('socketFront.debtWipeoutDefault', 'Your portfolio has been wiped out due to excessive debt.'), false);
  setTimeout(() => {
    window.location.href = data.redirectUrl || '/debtend.html';
  }, 2000);
}

/**
 * Handler for debt closed notification
 */
export function handleDebtClosed(data) {
  console.log('💰 Debt closed notification received:', data);
  const { instrumentType, instrumentId, message, amount } = data;
  const { showNotification } = require('./notif.js');

  const defaultMessage = instrumentType
    ? t('debt.closedDefault', 'A {type} debt instrument has been closed.').replace('{type}', instrumentType)
    : t('debt.closedGeneric', 'A debt instrument has been closed.');
  showNotification(message || defaultMessage, true);
}

/**
 * Register all debt popup handlers with a socket instance
 * @param {Socket} socket - The socket.io instance to register handlers with
 */
export function registerDebtPopupHandlers(socket) {
  // Altrau Z Bill handlers
  socket.on('altrauZBillReceived', handleAltrauZBillReceived);
  socket.on('altrauZBillGiven', handleAltrauZBillGiven);

  // M Note handlers
  socket.on('mNoteCreated', handleMNoteCreated);
  socket.on('mNoteProgress', handleMNoteProgress);
  socket.on('mNoteRepaid', handleMNoteRepaid);
  socket.on('mNoteTradeAlert', handleMNoteTradeAlert);
  socket.on('mNoteConvertible', handleMNoteConvertible);

  // Z-Payday CDO handlers
  socket.on('zPaydayCDOCreated', handleZPaydayCDOCreated);
  socket.on('zPaydayCDOReloaded', handleZPaydayCDOReloaded);
  socket.on('zPaydayCDOBankruptcy', handleZPaydayCDOBankruptcy);

  // V Note handlers
  socket.on('vNoteGranted', handleVNoteGranted);
  socket.on('vNoteGrantConfirm', handleVNoteGrantConfirm);

  // Generic debt notifications
  socket.on('debtNotification', handleDebtNotification);
  socket.on('debtWipeout', handleDebtWipeout);
  socket.on('debtClosed', handleDebtClosed);
}
